// World-space labels (§14.3, §24.4, §27.4): a pool of 8 nameplates (glyph + name + Dizzy %) placed
// each frame with `translate3d` from `worldToScreen(head + 0.35 m)`; hidden for out beans (ghosts
// at 50 %) and during replays (except the focus bean); Dizzy numbers follow the "Show Dizzy
// numbers" setting. Also the local mash prompt with its escape ring (§2.6: 10 + ⌊Dizzy/20⌋
// presses) and the offscreen arrows for your bean and your carrier/target (outside 92 % of the
// viewport, §24.4). Plates never pile up: in a cluster the nearer bean keeps its spot, a farther
// bean's plate stacks above it (eased, so it does not pop) or fades when the stack would grow too
// tall, ghost plates step aside while 3+ plates crowd one spot, and your own plate never moves.
import { MAX_BEANS, NO_SLOT, TUNE } from '@shared/constants';
import { BS } from '@shared/ids';
import type { BeanView, FrameView } from '../../game/FrameView';
import type { UiDeps } from '../deps';
import { actionCap } from '../glyphView';
import { icon, identityBadge, identityHex } from '../icons';
import { el, replayClass, setHidden, setStyle, setText, toggleClass } from '../ui';
import { dizzyTier } from './DizzyDial';

/** Bean height above its feet (§12.4: about 1.1 m), scaled by Big Shake. */
const BEAN_HEIGHT = 1.1;
const PLATE_LIFT = 0.35;
/** §24.4: the arrow shows when the bean is outside 92 % of the viewport. */
const ARROW_INSET = 0.04;
const ARROW_MARGIN_PX = 34;
/** Gap between stacked plates, px. */
const STACK_GAP_PX = 2;
/** A plate lifts at most this many of its own heights before it fades instead. */
const MAX_LIFT_PLATES = 2.2;
const FADED_OPACITY = 0.45;
/** How fast a plate eases to its stacked height (1/s). */
const LIFT_RATE = 14;

interface Plate {
  root: HTMLElement; badge: HTMLElement; badgeKey: string; name: HTMLElement; dizzy: HTMLElement; tag: HTMLElement;
  /** Measured size (px) and what it was measured for. */
  w: number; h: number; sizeKey: string;
  /** Eased lift above the anchor (px). */
  lift: number;
}

/** One plate to place this frame: its anchor (bottom center), size and stacking facts. */
interface PlateSpot { i: number; x: number; y: number; w: number; h: number; local: boolean; ghost: boolean; lift: number; faded: boolean }

function overlaps(a: PlateSpot, b: PlateSpot): boolean {
  return Math.abs(a.x - b.x) * 2 < a.w + b.w && a.y - a.lift - a.h < b.y - b.lift && b.y - b.lift - b.h < a.y - a.lift;
}

/**
 * De-overlaps plates in place (sets `lift` and `faded`). Ghost plates with 2+ other plates around
 * them are dropped (returned false in `keep`). The local plate is placed first and never moves; the
 * others go nearest first (larger screen y: the camera looks down at the arena), each lifting above
 * whatever it would cover, or fading where it would have to climb too far.
 */
export function stackPlates(spots: PlateSpot[], keep: boolean[]): void {
  for (const s of spots) {
    keep[s.i] = true;
    if (!s.ghost || s.local) continue;
    let crowd = 0;
    for (const o of spots) if (o !== s && Math.abs(o.x - s.x) < (o.w + s.w) * 0.6 && Math.abs(o.y - s.y) < (o.h + s.h) * 1.2) crowd++;
    if (crowd >= 2) keep[s.i] = false;
  }
  const order = spots.filter((s) => keep[s.i]).sort((a, b) => (a.local === b.local ? b.y - a.y : a.local ? -1 : 1));
  const placed: PlateSpot[] = [];
  for (const s of order) {
    s.lift = 0;
    s.faded = false;
    if (!s.local) {
      for (let guard = 0; guard < placed.length + 1; guard++) {
        const hit = placed.find((o) => !o.faded && overlaps(s, o));
        if (!hit) break;
        s.lift = s.y - (hit.y - hit.lift - hit.h - STACK_GAP_PX);
      }
      if (s.lift > s.h * MAX_LIFT_PLATES) {
        s.lift = 0;
        s.faded = true;
      }
    }
    placed.push(s);
  }
}
interface Arrow { root: HTMLElement; badge: HTMLElement; badgeKey: string; label: HTMLElement }

/** Escape presses needed while grabbed (§2.6). */
export function escapeNeeded(dizzy: number): number {
  return TUNE.grab.escapeBase + Math.floor(dizzy / 20) * TUNE.grab.escapePerDizzy20;
}

/**
 * Where an offscreen arrow sits: the bean's screen point clamped into the inset viewport, and the
 * angle (radians, 0 = right) from the screen center toward the bean. Null when the bean is on screen.
 */
export function arrowPlacement(x: number, y: number, w: number, h: number, inset = ARROW_INSET): { x: number; y: number; angle: number } | null {
  const x0 = w * inset;
  const y0 = h * inset;
  const x1 = w * (1 - inset);
  const y1 = h * (1 - inset);
  if (x >= x0 && x <= x1 && y >= y0 && y <= y1) return null;
  const cx = w / 2;
  const cy = h / 2;
  const dx = x - cx;
  const dy = y - cy;
  // Scale the center→bean ray onto the inset rectangle.
  const sx = dx !== 0 ? (dx > 0 ? x1 - ARROW_MARGIN_PX - cx : x0 + ARROW_MARGIN_PX - cx) / dx : Infinity;
  const sy = dy !== 0 ? (dy > 0 ? y1 - ARROW_MARGIN_PX - cy : y0 + ARROW_MARGIN_PX - cy) / dy : Infinity;
  const s = Math.min(Math.abs(sx), Math.abs(sy), 1);
  return { x: cx + dx * s, y: cy + dy * s, angle: Math.atan2(dy, dx) };
}

export interface Nameplates {
  readonly el: HTMLElement;
  update(view: FrameView, presented: FrameView, colorblind: boolean, showDizzy: boolean): void;
}

export function createNameplates(deps: UiDeps): Nameplates {
  const layer = el('div', { class: 'nameplates', attrs: { 'aria-hidden': 'true' } });
  const plates: Plate[] = Array.from({ length: MAX_BEANS }, () => {
    const badge = el('span');
    const name = el('span', { class: 'np-name' });
    const dizzy = el('span', { class: 'np-dizzy' });
    const tag = el('span', { class: 'np-tag' });
    const root = el('div', { class: 'np' }, el('div', { class: 'np-pill' }, badge, name, dizzy), tag);
    root.hidden = true;
    layer.append(root);
    return { root, badge, badgeKey: '', name, dizzy, tag, w: 0, h: 0, sizeKey: '', lift: 0 };
  });
  const spots: PlateSpot[] = [];
  const keep: boolean[] = new Array<boolean>(MAX_BEANS).fill(true);
  /** The kept plates' final rects for the renderer's text pops: [centerX, bottomY, w, h] each (reused). */
  const rects: number[] = [];
  let rectsShown = false;

  // Mash prompt over the local bean while grabbed.
  const mashCap = el('span', { class: 'mash-cap' });
  const mashRing = el('i', { class: 'mash-ring' });
  const mash = el('div', { class: 'mash' }, el('div', { class: 'mash-inner' }, el('div', { class: 'mash-dial' }, mashRing, el('span', { class: 'mash-text', text: 'MASH!' })), mashCap));
  mash.hidden = true;
  layer.append(mash);

  const arrows: Arrow[] = Array.from({ length: 2 }, (_, i) => {
    const badge = el('span');
    const label = el('span', { class: 'oa-label' });
    const root = el('div', { class: `offscreen-arrow${i === 0 ? ' me' : ''}` }, el('span', { class: 'oa-point' }, icon('arrowR')), el('span', { class: 'oa-body' }, badge, label));
    root.hidden = true;
    layer.append(root);
    return { root, badge, badgeKey: '', label };
  });

  const pt = { x: 0, y: 0, visible: false };
  let mashes = 0;
  let grabbedSince = -1;
  let mashCapDevice = '';
  let padPrev: boolean[] = [];

  // Any key, click/tap or pad button while grabbed is one escape input (§2.6).
  const onMash = (e: Event): void => {
    if (grabbedSince < 0) return;
    if (e instanceof KeyboardEvent && e.repeat) return;
    mashes++;
    replayClass(mash, 'bump');
  };
  window.addEventListener('keydown', onMash);
  window.addEventListener('pointerdown', onMash);

  function pollPadMash(): void {
    let pads: readonly (Gamepad | null)[] = [];
    try {
      pads = typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : [];
    } catch {
      return;
    }
    const pad = pads.find((p) => p && p.connected) ?? null;
    if (!pad) return;
    pad.buttons.forEach((b, i) => {
      const down = b.pressed || b.value > 0.5;
      if (down && !padPrev[i]) {
        mashes++;
        replayClass(mash, 'bump');
      }
      padPrev[i] = down;
    });
  }

  function headPoint(b: BeanView): { x: number; y: number; visible: boolean } {
    const s = b.radius > 0 ? b.radius / TUNE.bean.radius : 1;
    deps.services.worldToScreen(b.x, b.y, b.z + BEAN_HEIGHT * s + PLATE_LIFT, pt);
    return pt;
  }

  function place(node: HTMLElement, x: number, y: number): void {
    setStyle(node, 'transform', `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`);
  }

  function setBadge(holder: { badge: HTMLElement; badgeKey: string }, identity: number, colorblind: boolean, cls: string): void {
    const key = `${identity}:${colorblind}`;
    if (key === holder.badgeKey) return;
    holder.badgeKey = key;
    const nb = identityBadge(identity, colorblind, cls);
    holder.badge.replaceWith(nb);
    holder.badge = nb;
  }

  function updateArrow(a: Arrow, b: BeanView | null, label: string, colorblind: boolean, w: number, h: number): void {
    if (!b || !b.active || (b.out && !b.ghost)) {
      setHidden(a.root, true);
      return;
    }
    deps.services.worldToScreen(b.x, b.y, b.z + 0.6, pt);
    const p = arrowPlacement(pt.x, pt.y, w, h);
    setHidden(a.root, p === null);
    if (!p) return;
    setBadge(a, b.identity, colorblind, 'idbadge sm');
    setText(a.label, label);
    setStyle(a.root, '--id', identityHex(b.identity, colorblind));
    setStyle(a.root, 'transform', `translate3d(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px, 0)`);
    setStyle(a.root, '--a', `${p.angle.toFixed(3)}rad`);
  }

  return {
    el: layer,
    update(view, presented, colorblind, showDizzy) {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const replay = presented.replay;
      spots.length = 0;
      for (let i = 0; i < MAX_BEANS; i++) {
        const p = plates[i];
        const b = presented.beans[i];
        const live = view.beans[i];
        const hide = !b.active || (b.out && !b.ghost) || (replay !== null && b.slot !== replay.focusSlot)
          || b.state === BS.FALLING || b.state === BS.RESPAWN
          // The mash prompt replaces your own plate while you are carried.
          || (b.slot === view.localSlot && b.state === BS.GRABBED && replay === null);
        if (hide) {
          setHidden(p.root, true);
          continue;
        }
        const at = headPoint(b);
        if (!at.visible) {
          setHidden(p.root, true);
          continue;
        }
        setHidden(p.root, false);
        const ax = at.x;
        const ay = at.y;
        setBadge(p, b.identity, colorblind, 'idbadge xs');
        setText(p.name, b.name || `Bean ${b.slot + 1}`);
        const showD = showDizzy && !b.ghost;
        setHidden(p.dizzy, !showD);
        if (showD) {
          setText(p.dizzy, `${Math.round(b.dizzy)}%`);
          p.dizzy.dataset.tier = dizzyTier(b.dizzy);
        }
        toggleClass(p.root, 'ghost', b.ghost);
        toggleClass(p.root, 'me', b.slot === view.localSlot);
        toggleClass(p.root, 'potato', presented.mode.potatoSlot === b.slot);
        // §9.6: a dropped player's bean is driven by a bot while the slot is held; AFK likewise.
        const dto = deps.room.dto?.players[i] ?? null;
        const tag = !live.connected || (dto !== null && !dto.isBot && !dto.connected) ? 'reconnecting…' : dto?.afk ? 'AFK' : '';
        if (p.tag.dataset.k !== tag) {
          p.tag.dataset.k = tag;
          p.tag.replaceChildren(...(tag === '' ? [] : [icon('bot'), el('span', { text: tag })]));
        }
        setHidden(p.tag, tag === '');
        // Size: measured when what it shows changes (one layout read, not one per frame).
        const sizeKey = `${p.name.textContent}|${showD}|${tag}|${deps.settings().accessibility.uiScale}|${w}x${h}`;
        if (sizeKey !== p.sizeKey) {
          p.sizeKey = sizeKey;
          p.w = p.root.offsetWidth;
          p.h = p.root.offsetHeight;
        }
        spots.push({ i, x: ax, y: ay, w: p.w, h: p.h, local: b.slot === view.localSlot, ghost: b.ghost, lift: 0, faded: false });
      }

      // Stack crowded plates (eased per plate), then place them.
      stackPlates(spots, keep);
      const k = 1 - Math.exp(-LIFT_RATE * Math.max(0, Math.min(0.1, view.dt)));
      rects.length = 0;
      for (const sp of spots) {
        const p = plates[sp.i];
        if (!keep[sp.i]) {
          setHidden(p.root, true);
          p.lift = 0;
          continue;
        }
        p.lift += (sp.lift - p.lift) * (view.dt > 0 ? k : 1);
        if (Math.abs(sp.lift - p.lift) < 0.5) p.lift = sp.lift;
        place(p.root, sp.x, sp.y - p.lift);
        toggleClass(p.root, 'faded', sp.faded);
        rects.push(sp.x, sp.y - p.lift, p.w, p.h);
      }
      // INT-B's text pops keep clear of the plates where they actually are.
      if (rects.length > 0 || rectsShown) deps.services.setNameplateRects(rects.length > 0 ? rects : null);
      rectsShown = rects.length > 0;

      // Mash prompt (local only).
      const me = view.localSlot !== NO_SLOT ? view.beans[view.localSlot] : null;
      const grabbed = me !== null && me.active && me.state === BS.GRABBED && replay === null;
      if (grabbed && grabbedSince < 0) {
        grabbedSince = performance.now();
        mashes = 0;
        padPrev = [];
      } else if (!grabbed) grabbedSince = -1;
      setHidden(mash, !grabbed);
      if (grabbed && me) {
        pollPadMash();
        const dev = deps.services.input.device;
        if (dev !== mashCapDevice) {
          mashCapDevice = dev;
          mashCap.replaceChildren(actionCap(deps, 'mash'));
        }
        const at = headPoint(presented.beans[me.slot] ?? me);
        place(mash, at.x, at.y - 6);
        setStyle(mashRing, '--k', Math.min(1, mashes / escapeNeeded(me.dizzy)).toFixed(3));
      }

      // Offscreen arrows: your bean, and your carrier / target.
      const partner = me && me.active && (me.state === BS.GRABBED || me.state === BS.CARRYING) && me.partner !== NO_SLOT ? view.beans[me.partner] : null;
      // Placed where the renderer drew them (hitstop holds, tilted floors).
      updateArrow(arrows[0], replay === null && me ? presented.beans[me.slot] ?? me : null, 'YOU', colorblind, w, h);
      updateArrow(arrows[1], replay === null && partner ? presented.beans[partner.slot] ?? partner : null, me?.state === BS.GRABBED ? 'CARRIER' : 'TARGET', colorblind, w, h);
    },
  };
}
